import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import type { components } from "@/api/booqly-api";
import { formatMoney } from "@/lib/currency";
import { FieldPair } from "@/components/v2/DataTable";
import { StatusBadge } from "@/components/v2/StatusBadge";
import type { CustomFieldValue } from "@/lib/service-detail";
import { formatPreferredDate } from "@/lib/booking-date";

type Selection = {
  itemId: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
};

// `customFieldValues` + `selections` are present in the regenerated OpenAPI
// type but the regen widens `customFieldValues[].type` to `string`. We
// preserve the narrower union by stripping the regenerated property and
// re-attaching ours.
type BookingDetail = Omit<
  components["schemas"]["BookingDetail"],
  "customFieldValues" | "selections"
> & {
  customFieldValues?: CustomFieldValue[];
  selections?: { tier?: Selection | null; addons?: Selection[] };
  preferredDate?: string | null;
};

/**
 * Details tab body — Pencil `DSWj3` (the "Details" tab content).
 *
 * BOOKING DETAILS card with FieldPair rows + CUSTOM FIELDS card (placeholder
 * until the API exposes per-booking field values) + CLIENT card with avatar +
 * contact info + a "View" link to the clients page.
 *
 * The pencil "Edit fields" button at the top of the booking-details card
 * opens the same `EditBookingPanel` the footer Edit button does — wired via
 * the optional `onEdit` callback supplied by the parent panel.
 */
export function BookingDetailsTab({
  booking,
  onEdit,
}: {
  booking: BookingDetail;
  onEdit?: () => void;
}) {
  const customer = booking.customer;
  // Custom-field values are sourced from the booking itself (snapshot-based)
  // so deleted schema fields still render. The Custom Fields tab on the
  // service is for editing the schema; per-booking values are captured on
  // booking create.
  const fieldValues = booking.customFieldValues ?? [];
  const tier = booking.selections?.tier ?? null;
  const addons = booking.selections?.addons ?? [];
  // Operator-set label (e.g. "Gowns", "Package") wins. Fall back to "Plan"
  // — we don't reach for category-derivation here because the booking's
  // snapshot doesn't record which inventory category the tier came from.
  const tierTitle =
    (booking.service as { tierLabel?: string | null } | null | undefined)
      ?.tierLabel?.trim() || "Plan";

  const fullName =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
    "(walk-in)";
  const initials = fullName
    .split(" ")
    .map((p) => p[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");

  return (
    <div className="space-y-5">
      <Section
        title="Booking details"
        action={
          <button
            type="button"
            onClick={onEdit}
            disabled={!onEdit}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            <Pencil className="h-3 w-3" /> Edit fields
          </button>
        }
      >
        <FieldPair
          label="Date"
          value={
            booking.preferredDate ? formatPreferredDate(booking.preferredDate) : "—"
          }
        />
        <FieldPair label="Service" value={booking.service?.name ?? "—"} />
        <FieldPair label="Status" value={<StatusBadge label={booking.status ?? "—"} />} />
        {booking.notes && <FieldPair label="Notes" value={booking.notes} />}
      </Section>

      {(tier || addons.length > 0) && (
        <Section
          title={`${tierTitle}${addons.length > 0 ? " & add-ons" : ""}`}
          action={
            booking.service?.id ? (
              <Link
                to={`/services/${booking.service.id}/inventory`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                <Pencil className="h-3 w-3" /> Manage
              </Link>
            ) : null
          }
        >
          <ul className="divide-y -mx-4 -my-3">
            {tier && (
              <li className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    {tierTitle}
                  </p>
                  <p className="text-sm font-medium truncate">{tier.name}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums">
                  {formatMoney(tier.currency, tier.price)}
                </span>
              </li>
            )}
            {addons.map((a) => (
              <li
                key={a.itemId}
                className="flex items-center justify-between gap-3 px-4 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    Add-on
                  </p>
                  <p className="text-sm truncate">
                    {a.name}
                    {a.quantity > 1 && (
                      <span className="text-muted-foreground"> × {a.quantity}</span>
                    )}
                  </p>
                </div>
                <span className="text-sm tabular-nums">
                  +{formatMoney(a.currency, a.price)}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between gap-3 px-4 py-2.5 bg-muted/30">
              <p className="text-sm font-semibold">Total</p>
              <p className="text-sm font-bold tabular-nums">
                {formatMoney(booking.currency, booking.price)}
              </p>
            </li>
          </ul>
        </Section>
      )}

      {fieldValues.length > 0 && (
        <Section
          title="Custom fields"
          action={
            booking.service?.id ? (
              <Link
                to={`/services/${booking.service.id}/custom-fields`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                <Pencil className="h-3 w-3" /> Edit schema
              </Link>
            ) : null
          }
        >
          <div className="grid gap-x-8 gap-y-1 md:grid-cols-2">
            {fieldValues.map((f) => (
              <FieldPair
                key={f.fieldId}
                label={f.label}
                value={
                  f.value && f.value.trim() !== "" ? (
                    f.type === "checkbox" ? (
                      f.value === "true" ? "Yes" : "No"
                    ) : f.type === "date" || f.type === "datetime" ? (
                      formatPreferredDate(f.value)
                    ) : (
                      f.value
                    )
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )
                }
              />
            ))}
          </div>
        </Section>
      )}

      <Section
        title="Client"
        action={
          customer?.id ? (
            <Link
              to={`/clients?focus=${customer.id}`}
              className="text-xs text-primary hover:underline"
            >
              View →
            </Link>
          ) : null
        }
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-xs font-medium shrink-0">
            {initials || "—"}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium text-sm">{fullName}</p>
            {customer?.email && (
              <p className="text-xs text-muted-foreground truncate">{customer.email}</p>
            )}
            {customer?.phone && (
              <p className="text-xs text-muted-foreground">{customer.phone}</p>
            )}
          </div>
        </div>
      </Section>

      {/* Payments — ledger-style list + a summary strip showing the
          deposit-aware threshold + how much of it is already covered.
          Replaces the old single-payment card so deposits show alongside
          balance payments in one timeline. */}
      {(() => {
        const summary = (booking as unknown as {
          paymentSummary?: {
            totalPaid?: number | string;
            requiredToConfirm?: number | string;
            remainingToConfirm?: number | string;
            depositPolicy?: "NONE" | "PERCENT" | "AMOUNT";
            depositValue?: number | string | null;
            payments?: Array<{
              id: string;
              amount: number | string;
              currency: string;
              status: string;
              methodName?: string | null;
              rejectionReason?: string | null;
              receivedAt?: string | null;
              verifiedAt?: string | null;
            }>;
          };
        }).paymentSummary;
        const cur = booking.currency ?? "PHP";
        if (!summary) return null;
        const fmt = (n: number | string | null | undefined) =>
          formatMoney(cur, n);
        return (
          <Section title="Payments">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <PaymentStat label="Required" value={fmt(summary.requiredToConfirm)} />
              <PaymentStat label="Paid" value={fmt(summary.totalPaid)} />
              <PaymentStat
                label="Remaining"
                value={fmt(summary.remainingToConfirm)}
                highlight={Number(summary.remainingToConfirm ?? 0) <= 0 ? "ok" : "warn"}
              />
            </div>
            {summary.depositPolicy && summary.depositPolicy !== "NONE" && (
              <p className="text-[11px] text-muted-foreground mb-2">
                Deposit policy:{" "}
                {summary.depositPolicy === "PERCENT"
                  ? `${summary.depositValue ?? "?"}% of total`
                  : `Fixed ${fmt(summary.depositValue)}`}
              </p>
            )}
            {summary.payments && summary.payments.length > 0 ? (
              <ul className="rounded-md border bg-card divide-y">
                {summary.payments.map((p) => (
                  <li key={p.id} className="px-3 py-2.5 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold tabular-nums">
                          {formatMoney(p.currency, p.amount)}
                        </p>
                        <StatusBadge label={p.status} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {p.methodName ?? "—"}
                        {p.verifiedAt && (
                          <>
                            {" · verified "}
                            {new Date(p.verifiedAt).toLocaleString()}
                          </>
                        )}
                        {!p.verifiedAt && p.receivedAt && (
                          <>
                            {" · received "}
                            {new Date(p.receivedAt).toLocaleString()}
                          </>
                        )}
                      </p>
                      {p.rejectionReason && (
                        <p className="text-xs text-status-cancelled mt-0.5">
                          Rejection reason: {p.rejectionReason}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No payments recorded yet. Use the Record Payment button to add
                a deposit or full payment.
              </p>
            )}
          </Section>
        );
      })()}

      {booking.receipts && booking.receipts.length > 0 && (
        <Section title="Receipts">
          <ul className="space-y-2">
            {booking.receipts.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.mime} · {r.bytes ? Math.round(r.bytes / 1024) : 0} KB
                  </p>
                </div>
                <a
                  href={r.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Download
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          {title}
        </p>
        {action}
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function PaymentStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: "ok" | "warn";
}) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p
        className={`text-sm font-semibold tabular-nums mt-0.5 ${
          highlight === "ok"
            ? "text-status-completed"
            : highlight === "warn"
              ? "text-status-pending"
              : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
